/**
 * Base64 helpers — standard base64 (not URL-safe), matching the server.
 * Extracted from wisermail-web/src/services/crypto.ts (chunked to avoid stack
 * overflow on large payloads).
 *
 * Uses `btoa`/`atob` where the runtime provides them (browsers, Node ≥16) and
 * falls back to a pure-JS codec on runtimes that don't (React Native/Hermes).
 */

const CHUNK_SIZE = 8192;

const B64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const B64_LOOKUP: Int16Array = (() => {
  const table = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64_ALPHABET.length; i++) {
    table[B64_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

// Checked per-call (not at import) so it stays correct however the host
// orders its polyfill loading relative to this module.
function hasBtoa(): boolean {
  return typeof btoa === 'function' && typeof atob === 'function';
}

function encodePureJs(data: Uint8Array): string {
  const out: string[] = [];
  const len = data.byteLength;
  for (let i = 0; i < len; i += 3) {
    const b0 = data[i];
    const b1 = i + 1 < len ? data[i + 1] : 0;
    const b2 = i + 2 < len ? data[i + 2] : 0;
    out.push(
      B64_ALPHABET[b0 >> 2],
      B64_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)],
      i + 1 < len ? B64_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)] : '=',
      i + 2 < len ? B64_ALPHABET[b2 & 0x3f] : '=',
    );
  }
  return out.join('');
}

function decodePureJs(b64: string): Uint8Array {
  let end = b64.length;
  while (end > 0 && b64[end - 1] === '=') end--;
  const outLen = Math.floor((end * 3) / 4);
  const bytes = new Uint8Array(outLen);
  let acc = 0;
  let accBits = 0;
  let o = 0;
  for (let i = 0; i < end; i++) {
    const code = b64.charCodeAt(i);
    const v = code < 128 ? B64_LOOKUP[code] : -1;
    if (v === -1) throw new Error(`invalid base64 character at index ${i}`);
    acc = (acc << 6) | v;
    accBits += 6;
    if (accBits >= 8) {
      accBits -= 8;
      bytes[o++] = (acc >> accBits) & 0xff;
    }
  }
  return bytes;
}

export function toBase64(data: Uint8Array): string {
  if (!hasBtoa()) return encodePureJs(data);
  const chunks: string[] = [];
  for (let i = 0; i < data.byteLength; i += CHUNK_SIZE) {
    const end = Math.min(i + CHUNK_SIZE, data.byteLength);
    chunks.push(String.fromCharCode(...data.subarray(i, end)));
  }
  return btoa(chunks.join(''));
}

export function fromBase64(b64: string): Uint8Array {
  if (!hasBtoa()) return decodePureJs(b64);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += CHUNK_SIZE) {
    const end = Math.min(i + CHUNK_SIZE, binary.length);
    for (let j = i; j < end; j++) {
      bytes[j] = binary.charCodeAt(j);
    }
  }
  return bytes;
}
